import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { requestPasswordReset, resetPassword } from '../../api/auth';
import { apiErrorMessage } from '../../api/client';
import { AuthInput } from '../../components/auth/AuthInput';
import { PasswordInput } from '../../components/auth/PasswordInput';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Step 1: request a 6-digit code. Step 2: code + new password (twice). */
export default function ResetPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<{
    email?: string;
    code?: string;
    password?: string;
    confirm?: string;
  }>({});
  const [submitting, setSubmitting] = useState(false);

  async function onRequest(e: FormEvent) {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      setErrors({ email: 'Enter a valid email address' });
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      toast.success('If that email exists, a reset code is on its way');
      setStep('reset');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not request a reset'));
    } finally {
      setSubmitting(false);
    }
  }

  async function onReset(e: FormEvent) {
    e.preventDefault();
    const next: typeof errors = {};
    if (!EMAIL_RE.test(email)) next.email = 'Enter a valid email address';
    if (!/^\d{6}$/.test(code)) next.code = 'Enter the 6-digit code from your email';
    if (password.length < 8) next.password = 'At least 8 characters';
    if (confirm !== password) next.confirm = "Passwords don't match";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    try {
      await resetPassword(email, code, password);
      // No auto-login after reset (spec 3.5) — back to the login page.
      toast.success('Password updated — sign in with your new password');
      navigate('/login');
    } catch (err) {
      setErrors({ code: apiErrorMessage(err, 'Reset failed — request a new code') });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section data-testid="page-resetpassword" className="mx-auto max-w-sm pt-6">
      <h1 className="text-2xl font-bold">
        {step === 'request' ? 'Reset password' : 'Choose a new password'}
      </h1>
      {step === 'request' ? (
        <>
          <p className="mt-2 text-sm text-stone-600">
            We'll email a 6-digit code to your verified address.
          </p>
          <form onSubmit={onRequest} noValidate className="mt-6 space-y-4">
            <AuthInput
              label="Email"
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={errors.email}
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-emerald-700 px-4 py-2.5 font-medium text-white hover:bg-emerald-800 disabled:opacity-50"
            >
              {submitting ? 'Sending…' : 'Send reset code'}
            </button>
          </form>
          <p className="mt-4 text-sm text-stone-600">
            Already have a code?{' '}
            <button
              type="button"
              onClick={() => setStep('reset')}
              className="font-medium text-emerald-700 hover:underline"
            >
              Enter it here
            </button>
          </p>
        </>
      ) : (
        <>
          <p className="mt-2 text-sm text-stone-600">
            Enter the code we emailed to <span className="font-semibold">{email || 'you'}</span> and
            pick a new password.
          </p>
          <form onSubmit={onReset} noValidate className="mt-6 space-y-4">
            {EMAIL_RE.test(email) ? null : (
              <AuthInput
                label="Email"
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={errors.email}
              />
            )}
            <AuthInput
              label="Reset code"
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              error={errors.code}
            />
            <PasswordInput
              label="New password"
              id="new_password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={errors.password}
            />
            <PasswordInput
              label="Confirm new password"
              id="confirm_password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              error={errors.confirm}
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-emerald-700 px-4 py-2.5 font-medium text-white hover:bg-emerald-800 disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Set new password'}
            </button>
          </form>
          <p className="mt-4 text-sm text-stone-600">
            Didn't get a code?{' '}
            <button
              type="button"
              onClick={() => setStep('request')}
              className="font-medium text-emerald-700 hover:underline"
            >
              Request one
            </button>
          </p>
        </>
      )}
      <p className="mt-4 text-sm">
        <Link to="/login" className="text-emerald-700 hover:underline">
          Back to sign in
        </Link>
      </p>
    </section>
  );
}
